import { BaseRowData } from '@adv-frontend/data-provider';
import { BaseFilterProps, Filter, FilterSuggestProps } from '../../lib/types/Filters';
import { OperatorType } from '../../lib/types/Operator';
import { SelectWithSuggestFilter } from './SelectWithSuggestFilter';
import { i18n } from './createFilterSelectWithSuggest.i18n';
import { isValidOperator } from '../../lib/helpers/reconcile/isValidOperator';
import { isValuesOfValueType } from '../../lib/helpers/isValuesOfValueType';
import { ALL_OPERATORS } from '../../lib/constants';
import { validateNotEmpty } from '../createFilterInput/validate/validateNotEmpty';

/**
 * Пропсы для создания фильтра с мультивыбором и cаджестом
 * @typeParam FilterId Идентификатор фильтра
 * @typeParam ValueType Тип значений в селекте
 */
interface CreateFilterSelectWithSuggestProps<
    FilterId extends string,
    RowData extends BaseRowData,
    ValueType extends string = string,
> extends BaseFilterProps<FilterId> {
    /** Пропсы для создания саджеста */
    suggestProps: FilterSuggestProps<RowData> & {
        /** Кол-во значений после которых сворачиваем фильтр*/
        overflowValuesLimit?: number;
        /** Функция рендера текста для свернутого фильтра*/
        renderOverflowValuesText?: (restCount: number) => string;
        /** Флаг отображения переключателя между отображением всех и только выбранных значений */
        showSelectedOnlySwitcher?: boolean;
    };
    /** Список доступных операторов, если не пришло - доступны все */
    operators?: OperatorType[];
    /** Пользовательские названия операторов */
    operatorTitles?: Partial<Record<OperatorType, string>>;
    /** Максимальное допустимое количество выбранных значений */
    maxCountSelectedItems?: number;
    /** Объект с допустимыми значениями фильтра */
    valueType?: Record<string, ValueType>;
}

/**
 * Фабрика, создающая фильтр с мультивыбором и cаджестом,
 * который можно передать как Filter в FiltersDeclaration
 * @typeParam FilterId идентификатор фильтра
 * @typeParam RowData тип значений в селекте
 * @param props пропсы для создания фильтра
 * @param props.id id фильтра
 * @param props.title название фильтра
 * @param props.operators список доступных операторов
 * @param props.operatorTitles пользовательские названия операторов
 * @param props.suggestProps пропсы для создания саджеста
 * @param props.suggestProps.dataProvider dataProvider для саджеста
 * @param props.suggestProps.useTitleByValue хук для запроса элементов по id для отображения в выбранных чипсах
 * @param props.suggestProps.renderSelectListItem функция для рендера элементов списка в селекте
 * @param props.suggestProps.renderFilterChipsItem функция для рендера выбранных чипсов
 * @returns Декларация фильтра
 * @kind helper
 */
export function createFilterSelectWithSuggest<
    FilterId extends string,
    RowData extends BaseRowData,
    ValueType extends string = string,
>({
    id,
    operators,
    operatorTitles,
    suggestProps: {
        dataProvider,
        useTitleByValue,
        renderSelectListItem,
        renderFilterChipsItem,
        overflowValuesLimit,
        renderOverflowValuesText,
        suggestPlaceholder,
        showSelectedOnlySwitcher,
    },
    maxCountSelectedItems,
    valueType,
    ...filterProps
}: CreateFilterSelectWithSuggestProps<FilterId, RowData, ValueType>): Filter<FilterId, ValueType[]> {
    return {
        ...filterProps,
        id,
        reconcile: ({ operator, value }) => {
            const filteredValue = value.filter(item => item !== '');

            if (
                !isValidOperator(operator, operators || ALL_OPERATORS) ||
                !isValuesOfValueType(filteredValue, valueType)
            ) {
                return null;
            }

            if (filteredValue.length === 0) {
                return null;
            }

            return { value: filteredValue, operator };
        },

        validate: ({ value }) => {
            if (filterProps.isRequired) {
                return validateNotEmpty(value);
            }

            if (maxCountSelectedItems !== undefined) {
                return value.length > maxCountSelectedItems
                    ? { errors: [i18n('Выбрано слишком много значений')] }
                    : null;
            }

            return null;
        },
        render: function Render(props) {
            return (
                <SelectWithSuggestFilter
                    {...props}
                    isRequired={filterProps.isRequired}
                    errors={props.errors}
                    dataProvider={dataProvider}
                    useTitleByValue={useTitleByValue}
                    renderSelectListItem={renderSelectListItem}
                    renderFilterChipsItem={renderFilterChipsItem}
                    shownValuesLimit={overflowValuesLimit}
                    renderValuesLimitText={renderOverflowValuesText}
                    suggestPlaceholder={suggestPlaceholder}
                    showSelectedOnlySwitcher={showSelectedOnlySwitcher}
                    id={id}
                    valueType={valueType}
                    operatorTitles={operatorTitles}
                />
            );
        },

        onlyOneFilterItem: true,
        hasValue: ({ value }) => value.length > 0,
        getDefaultValue: availableOperators => ({ value: [], operator: availableOperators[0] }),
        getDescription: ({ value }) => `${filterProps.title}: ${i18n('выбрано {count}', { count: value.length })}`,
        operators,
    };
}
